import { useEffect, useState } from 'react'

interface PerformanceMetrics {
  loadTime: number
  firstContentfulPaint: number
  largestContentfulPaint: number
  firstInputDelay: number
  cumulativeLayoutShift: number
}

export function usePerformance() {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null)
  const [isSupported, setIsSupported] = useState(false)

  useEffect(() => {
    // Check if Performance Observer is supported
    if (!('PerformanceObserver' in window)) {
      return
    }

    setIsSupported(true)

    const getPerformanceMetrics = () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
      const paint = performance.getEntriesByType('paint')
      
      const loadTime = navigation.loadEventEnd - navigation.loadEventStart
      const firstContentfulPaint = paint.find(entry => entry.name === 'first-contentful-paint')?.startTime || 0
      
      setMetrics({
        loadTime,
        firstContentfulPaint,
        largestContentfulPaint: 0, // Will be updated by LCP observer
        firstInputDelay: 0, // Will be updated by FID observer
        cumulativeLayoutShift: 0, // Will be updated by CLS observer
      })
    }

    // Observe Largest Contentful Paint
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      const lastEntry = entries[entries.length - 1]
      setMetrics(prev => prev ? { ...prev, largestContentfulPaint: lastEntry.startTime } : null)
    })
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })

    // Observe First Input Delay
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      entries.forEach((entry: any) => {
        setMetrics(prev => prev ? { ...prev, firstInputDelay: entry.processingStart - entry.startTime } : null)
      })
    })
    fidObserver.observe({ entryTypes: ['first-input'] })

    // Observe Cumulative Layout Shift
    let clsValue = 0
    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value
          setMetrics(prev => prev ? { ...prev, cumulativeLayoutShift: clsValue } : null)
        }
      })
    })
    clsObserver.observe({ entryTypes: ['layout-shift'] })

    // Get initial metrics
    if (document.readyState === 'complete') {
      getPerformanceMetrics()
    } else {
      window.addEventListener('load', getPerformanceMetrics)
    }

    return () => {
      lcpObserver.disconnect()
      fidObserver.disconnect()
      clsObserver.disconnect()
    }
  }, [])

  return { metrics, isSupported }
}

// Hook for monitoring network performance
export function useNetworkPerformance() {
  const [connection, setConnection] = useState<{
    effectiveType: string
    downlink: number
    rtt: number
  } | null>(null)

  useEffect(() => {
    if ('connection' in navigator) {
      const conn = (navigator as any).connection
      setConnection({
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
      })

      const updateConnection = () => {
        setConnection({
          effectiveType: conn.effectiveType,
          downlink: conn.downlink,
          rtt: conn.rtt,
        })
      }

      conn.addEventListener('change', updateConnection)
      return () => conn.removeEventListener('change', updateConnection)
    }
  }, [])

  return connection
}
